//
//  CsvExport.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import { ReactElement, useState, SyntheticEvent } from "react";
import { Container, Alert, AlertColor, Snackbar, Box, Tabs, Tab, Paper, Typography } from "@mui/material";
import { SectionContainer, SectionRow } from "./component/Section";
import { DateRangePicker } from "./component/DateRangePicker";
import { CsvSummarySelector } from "./component/CsvSummarySelector";
import { CsvExportButton } from "./component/CsvExportButton";
import { CsvExportButtonList } from "./component/CsvExportButtonList";
import useActionAlert from "./hooks/useActionAlert";
import useDateRange from "./hooks/useDateRange";
import useCsvSummarySelector from "./hooks/useCsvSummarySelector";
import useCsvExportButton from "./hooks/useCsvExportButton";
import { getToday } from "./utils/getToday";
import { FormButton } from "./component/FormButton";
import { CONTACT_URL, isIntradayNeeded } from "./utils/env";
import BackToAdminButton from "./component/BackToAminMainButton";
import CsvDownloadList from "./CsvDownloadList";
import { PageTitle } from "./component/PageTitle";
import TitleBar from "./component/TitleBar";
import { CsvType } from "./constants/csv";


const CsvExport = (): ReactElement => {
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const { message, type, showMessage } = useActionAlert();
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    errorMsg: dateRangeErrorMsg,
  } = useDateRange(
    getToday().subtract(7, "day"),
    getToday().subtract(1, "day")
  );
  const {
    selectedCsvSummaryItems,
    setSelectedCsvSummaryItems,
    errorMsg: summaryErrorMsg,
  } = useCsvSummarySelector();
  const csvExportButtonProps = useCsvExportButton({
    showMessage,
    startDate,
    endDate,
    selectedCsvSummaryItems,
  });

  return (
    <>
      <TitleBar contactUrl={CONTACT_URL} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          width: "100%",
          padding: "10px 0",
        }}
      >
        <BackToAdminButton />
      </Box>

      <Container
        sx={{
          my: 5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <PageTitle title="CSVダウンロード" />
        <Paper
          elevation={3}
          sx={{
            width: '100%',
            maxWidth: '700px',
            overflow: 'visible',
            border: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%', maxWidth: '800px', mb: 4 }}>
            <Tabs value={tabIndex} onChange={handleTabChange} centered>
              <Tab label="CSV作成リクエスト" />
              <Tab label="作成済みCSV一覧" />
            </Tabs>
          </Box>
          {tabIndex === 0 && (
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '100%',
                  bgcolor: 'rgba(255, 255, 255, 0.85)', 
                  backdropFilter: 'blur(12px)',
                  pt: 4, 
                  px: 4, 
                  pb: 2  
                }}
              >
                <SectionContainer width="100%">

                  <SectionRow title="期間指定" direction="column">
                    <Box display="flex" justifyContent="center" width="100%" gap={2} >
                      <DateRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        errorMsg={dateRangeErrorMsg}
                      />
                    </Box>
                  </SectionRow>
                </SectionContainer>
              </Box>
              <Box sx={{ width: '100%', p: 4 }}> 
                <SectionContainer width="100%">

                  <SectionRow title="日次データ" direction="column" >
                    <Box width="100%" >
                      <FormButton
                        button={
                          <Box display="flex" width="100%" justifyContent="center">
                            <CsvExportButton
                              csvType={CsvType.Summary}
                              {...csvExportButtonProps}
                              disabled={!!dateRangeErrorMsg || !!summaryErrorMsg}
                            />
                          </Box>
                        }
                      >
                        <CsvSummarySelector
                          selectedCsvSummaryItems={selectedCsvSummaryItems}
                          setSelectedCsvSummaryItems={setSelectedCsvSummaryItems}
                          errorMsg={summaryErrorMsg}
                        />
                      </FormButton>
                    </Box>
                  </SectionRow>

                  {isIntradayNeeded && (
                    <SectionRow title="時系列データ(Intraday)" direction="column">
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        ダウンロードしたいデータ項目のリクエストボタンを押してください。
                      </Typography>
                      <Box display="flex" width="100%" justifyContent="center">
                        <CsvExportButtonList
                          {...csvExportButtonProps}
                          disabled={!!dateRangeErrorMsg}
                        />
                      </Box>
                    </SectionRow>
                  )}
                </SectionContainer>
              </Box>
            </Box>
          )}
          {tabIndex === 1 && (
            <Box width="100%" maxWidth="md" pb={4}>
              <CsvDownloadList />
            </Box>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={!!message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={type as AlertColor} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CsvExport;